import * as Sentry from '@sentry/vue';

const SENTRY_DSN =
  'https://ecff484742acb1652bb3635814981639@o4510585824804864.ingest.us.sentry.io/4511300448550912';

function detectEnvironment(): string {
  if (typeof window === 'undefined') return 'production';

  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1') return 'development';
  if (host === 'apostatudo.bet.br') return 'production';
  if (host === 'performance.apostatudo.bet.br') return 'staging';
  if (host.endsWith('.cactusgaming.net')) return 'staging';

  return 'unknown';
}

export default defineNuxtPlugin((nuxtApp: any) => {
  // Sentry desativado temporariamente — reativar removendo este return.
  return;

  const env = detectEnvironment();

  if (env === 'development') return;

  const router = useRouter();

  Sentry.init({
    app: nuxtApp.vueApp,
    dsn: SENTRY_DSN,
    environment: env,
    release: __SENTRY_RELEASE__,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: env === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
    ignoreErrors: [
      // Bridges nativas (WebView Android/iOS, Telegram WebApp, in-app
      // browsers). Não temos como prevenir — o ambiente hospedeiro decide
      // se o método existe e quando o objeto é destruído. Cobre variantes
      // observadas em produção: post, postEvent, postMessage,
      // enableButtonsClickedMetaDataLogging.
      /Error invoking \w+: Method not found/i,
      /Java object is gone/i,
      /window\.webkit\.messageHandlers/i,
      /iabjs_unified_bridge/i,
      /TelegramWebviewProxy/i,
      // Turnstile com container nulo: o componente AntiRobot já captura
      // localmente e cai no fallback do reCAPTCHA. Não é erro acionável.
      /\[Cloudflare Turnstile\]/i,
      /TurnstileError/i,
      // FetchError 400 em /documents/validate: fluxo de negócio esperado
      // (CPF inválido / não encontrado). O ofetch rejeita antes do catch
      // local consumir, caindo no unhandledrejection global.
      /FetchError:.*\/(?:api\/)?documents\/validate.*:\s*400/i,
      // FetchError 401 em endpoints autenticados quando a sessão expirou:
      // logout, user-profile e wallet. São idempotentes ou o caller já
      // trata o re-auth — não é acionável.
      /FetchError:.*\/(?:api\/)?auth\/(?:logout|user-profile).*:\s*401/i,
      /FetchError:.*\/(?:api\/)?users\/wallet.*:\s*401/i,
      // 404 com HTML malformado vindo de iframe de provedor de jogo que
      // corrompe a URL do parent. Sintoma: "Page not found:
      // /games/PROVIDER/<!DOCTYPE...".
      /Page not found:.*%3C!DOCTYPE/i,
      // Bots e scanners varrendo paths conhecidos de outros stacks
      // (WordPress, painéis admin). Geram 404 sem ação possível.
      /Page not found:\s*\/(wp-admin|wp-login|wp-content|admin|phpmyadmin)/i,
    ],
    beforeSend(event, hint) {
      const headers = event.request?.headers as
        | Record<string, string>
        | undefined;

      if (headers) {
        delete headers.Authorization;
        delete headers.authorization;
      }

      // "TypeError: Failed to fetch" / "Load failed" sem stack first-party
      // é cancelamento de navegação SPA ou fetch de script externo perdendo
      // conexão. Sem frames in_app não há nada acionável.
      const error = hint?.originalException as { message?: string } | undefined;

      const message =
        error?.message || event.exception?.values?.[0]?.value || '';

      const isUnactionableFetchError =
        /^TypeError: (Failed to fetch|Load failed)/.test(message) &&
        !event.exception?.values?.[0]?.stacktrace?.frames?.some(
          (f) => f.in_app,
        );

      if (isUnactionableFetchError) {
        return null;
      }

      return event;
    },
  });

  function buildContext() {
    try {
      return {
        currentUrl: location.href,
        currentPath: location.pathname,
        currentSearch: location.search,
        currentHash: location.hash,
        referrer: document.referrer || '(empty)',
        userAgent: navigator.userAgent,
        isStandalonePwa:
          window.matchMedia?.('(display-mode: standalone)')?.matches ?? false,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
      };
    } catch {
      return {};
    }
  }

  nuxtApp.hook('vue:error', (err: any, _instance: any, info: any) => {
    Sentry.captureException(err, {
      tags: { source: 'vue:error', vueInfo: String(info || '') },
      extra: { info, ...buildContext() },
    });
  });

  nuxtApp.hook('app:error', (err: any) => {
    Sentry.captureException(err, {
      tags: { source: 'app:error' },
      extra: buildContext(),
    });
  });

  // Vue por padrão chama o errorHandler global e re-lança o erro, fazendo
  // Nuxt cair no error.vue. Aqui consumimos erros de mounted/render para
  // que um componente quebrado não derrube a página inteira — só ele
  // mesmo deixa de funcionar e o erro é reportado pro Sentry.
  const previousErrorHandler = nuxtApp.vueApp.config.errorHandler;

  nuxtApp.vueApp.config.errorHandler = (
    err: unknown,
    instance: any,
    info: string,
  ) => {
    try {
      Sentry.captureException(err, {
        tags: { source: 'vue.errorHandler', vueInfo: String(info || '') },
        extra: { info, ...buildContext() },
      });
    } catch {}

    if (typeof previousErrorHandler === 'function') {
      try {
        previousErrorHandler(err, instance, info);
      } catch {}
    }
  };

  // Erros de chunk são auto-tratados pelo plugin chunk-reload.client.ts
  // (purga SW/caches e dá reload). Reportamos como warning para manter
  // visibilidade do volume de stale bundles pós-deploy sem soar alarme.
  nuxtApp.hook('app:chunkError', (payload: any) => {
    Sentry.captureMessage('Chunk load error', {
      level: 'warning',
      tags: { source: 'app:chunkError' },
      extra: {
        chunkError: payload?.error?.message || String(payload?.error || ''),
        chunkStack: payload?.error?.stack,
        ...buildContext(),
      },
    });
  });
});
